import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Cookie } from 'lucide-react';
import { openCookieConsent } from '../components/CookieConsent';
import useSEO from '../hooks/useSEO';

const sections = [
  {
    number: '01',
    title: 'Qui est responsable de vos données ?',
    content: `Le responsable du traitement est :

Emma Gandolfi — Entrepreneur individuel (CyberGuard)
SIRET : 10235229100011
La Côte-Saint-André, France
Email : contact@cyber-guard.fr

Pour toute question sur vos données ou pour exercer vos droits, écrivez à cette adresse.`,
  },
  {
    number: '02',
    title: 'Quelles données collectons-nous ?',
    content: `• Votre compte : nom, adresse email, mot de passe (stocké uniquement sous forme chiffrée avec bcrypt, jamais en clair), plan d'abonnement, date de création.
• La sécurité de votre compte : la date de confirmation de votre adresse email et, si vous activez la double authentification, son secret (stocké chiffré) et vos codes de secours (stockés uniquement sous forme d'empreinte).
• Votre utilisation du service : les domaines que vous faites analyser, les résultats des audits, les rapports IA générés, et vos clés API si vous êtes en plan Enterprise.
• Vos domaines et leur surveillance : les domaines ajoutés dans « Mes domaines », leur statut de vérification, les surveillances hebdomadaires programmées avec leurs derniers résultats, et l'historique des alertes envoyées (expiration de certificat, baisse de score).
• Si vous êtes une agence (plan Enterprise) : le nom, le logo, la couleur, l'email et le site web de votre agence ; les fiches clients que vous créez (nom du client, nom et email du contact, notes) ; les liens de partage de vos rapports (dates de création et d'expiration, nombre et date de la dernière consultation). Les consultations ne sont pas rattachées à l'adresse IP des visiteurs ; comme pour toute requête, cette adresse peut apparaître dans les journaux techniques de l'hébergeur.
• Votre paiement : votre carte bancaire est saisie directement chez Stripe, CyberGuard n'y a jamais accès. Nous conservons seulement votre identifiant client Stripe et l'état de votre abonnement.
• Données techniques : votre adresse IP, utilisée pour limiter les tentatives abusives, inscrite dans nos journaux de sécurité en cas d'événement suspect (par exemple un lien de réinitialisation invalide ou un code de double authentification erroné), et présente dans les journaux techniques de notre hébergeur.
• Mesure d'audience : uniquement si vous l'acceptez, Google Analytics collecte des informations sur votre navigation (pages vues, type d'appareil, pays approximatif).
• Liste d'attente : si vous vous êtes inscrit pour être prévenu du lancement, votre adresse email.

Nous ne collectons aucune donnée sensible au sens de l'article 9 du RGPD.

À propos des sites analysés : un audit CyberGuard ne lit que des informations publiques du domaine que vous saisissez (certificat SSL, en-têtes HTTP, enregistrements DNS, pages légales). Il ne collecte pas les données des visiteurs de ce site.

L'analyse approfondie (plans Pro et Enterprise, sur un domaine vérifié) va plus loin, sans rien exploiter : elle vérifie si quelques fichiers sensibles connus sont accessibles publiquement (par exemple /.env ou une sauvegarde), en ne lisant que leurs premiers octets pour confirmer leur nature. Leur contenu n'est jamais conservé : seul le constat « fichier exposé » figure dans le rapport. Elle teste aussi les versions de chiffrement acceptées par le serveur, et elle recherche les sous-domaines publics dans les journaux de certificats via le service crt.sh, auquel seul le nom de domaine est transmis.`,
  },
  {
    number: '03',
    title: 'Pourquoi et sur quelle base ?',
    content: `• Créer et gérer votre compte, réaliser vos audits et générer vos rapports : exécution du contrat.
• Gérer votre abonnement et la facturation : exécution du contrat, et obligation légale pour la conservation des pièces comptables.
• Vous envoyer les emails liés au service (bienvenue et confirmation de votre adresse, réinitialisation du mot de passe, confirmation d'abonnement, alertes de surveillance et d'expiration de certificat) : exécution du contrat.
• Vous envoyer au plus deux emails de suivi si votre compte est gratuit (un rappel si votre audit offert n'est pas lancé, puis un bilan de votre audit) : intérêt légitime à vous accompagner dans l'utilisation du service. Chaque email contient un lien de désinscription en un clic.
• Vérifier votre adresse email et proposer la double authentification : exécution du contrat et intérêt légitime à protéger votre compte. La double authentification est obligatoire pour l'accès administrateur.
• Gérer les fiches clients et les liens de partage des agences : exécution du contrat avec l'agence (voir la section 05).
• Protéger la plateforme contre les abus (limitation des tentatives, journaux de sécurité) : intérêt légitime à assurer la sécurité du service.
• Mesurer l'audience du site avec Google Analytics : votre consentement, que vous pouvez retirer à tout moment.
• Vous prévenir du lancement ou des nouveautés si vous êtes sur la liste d'attente : votre consentement.`,
  },
  {
    number: '04',
    title: 'Rapports générés par intelligence artificielle',
    content: `Les rapports IA sont générés par le modèle LLaMA via le service Groq. Seules les données techniques de l'audit lui sont transmises : le nom de domaine analysé, le score et les points détectés. Votre nom et votre adresse email ne sont jamais envoyés.

Ces rapports sont une aide à la compréhension. Ils ne produisent aucune décision automatisée ayant un effet juridique sur vous au sens de l'article 22 du RGPD.`,
  },
  {
    number: '05',
    title: 'Agences : les données de vos clients',
    content: `Si vous utilisez CyberGuard pour vos propres clients (plan Enterprise), c'est vous qui décidez quelles informations sur eux vous enregistrez dans les fiches clients et quels rapports vous leur partagez. Pour ces données, vous êtes responsable du traitement et CyberGuard agit comme sous-traitant au sens de l'article 28 du RGPD : nous ne les utilisons que pour vous fournir le service, nous ne les consultons pas à d'autres fins, et elles sont supprimées avec votre compte ou dès que vous supprimez la fiche.

Un lien de partage permet à toute personne qui le possède de consulter le rapport concerné jusqu'à sa date d'expiration (un an au plus) ou jusqu'à ce que vous le désactiviez. La page de partage ne dépose aucun cookie et ne contient aucune mesure d'audience. Il vous appartient d'informer vos clients du traitement de leurs données et de ne transmettre ces liens qu'aux bonnes personnes.`,
  },
  {
    number: '06',
    title: 'Qui peut accéder à vos données ?',
    content: `Seule la responsable du traitement dispose d'un accès administrateur. Vos données sont aussi traitées par les sous-traitants suivants, uniquement pour faire fonctionner le service :

• MongoDB Atlas : base de données (comptes, audits, rapports), serveurs AWS à Paris, France.
• Render : serveur applicatif, région Francfort, Allemagne.
• Vercel : diffusion de l'interface web via un réseau mondial.
• Stripe : paiement et gestion des abonnements.
• Resend : envoi des emails (votre adresse email et le contenu du message).
• Groq : génération des rapports IA (données techniques de l'audit uniquement).
• crt.sh (Sectigo) : recherche des sous-domaines publics lors de l'analyse approfondie (nom de domaine uniquement).
• Google : mesure d'audience, uniquement si vous l'avez acceptée.

Vos comptes, audits et rapports sont stockés dans l'Union européenne. Vercel, Stripe, Resend, Groq et Google sont des sociétés établies aux États-Unis et peuvent traiter certaines données hors de l'Union européenne. Ces transferts sont encadrés par le cadre de protection des données UE–États-Unis (Data Privacy Framework) et/ou par les clauses contractuelles types de la Commission européenne, prévus par leurs accords de traitement des données.

Nous ne vendons ni ne louons jamais vos données.`,
  },
  {
    number: '07',
    title: 'Combien de temps les conservons-nous ?',
    content: `• Compte, audits et rapports : tant que votre compte existe. Ils sont supprimés sur simple demande, ou 3 ans après votre dernière activité.
• Données de facturation : 10 ans, conformément aux obligations comptables.
• Journaux de sécurité : 12 mois au maximum.
• Choix sur les cookies : 6 mois, après quoi nous vous redemandons votre accord.
• Cookies Google Analytics : 13 mois au maximum. Données de mesure d'audience : 14 mois au maximum.
• Liste d'attente : jusqu'à votre désinscription, et au plus 3 ans.
• Lien de confirmation d'email : 48 heures. Lien de réinitialisation du mot de passe : 1 heure.
• Fiches clients et marque d'agence : jusqu'à leur suppression par l'agence, ou la suppression du compte.
• Liens de partage : actifs jusqu'à leur expiration (un an au plus) ou leur désactivation ; leur historique est supprimé avec le compte.`,
  },
  {
    number: '08',
    title: 'Cookies et stockage dans votre navigateur',
    content: `• Jeton de connexion (stockage local du navigateur) : vous garde connecté. Indispensable au service, il ne nécessite pas votre consentement.
• Votre choix sur les cookies (stockage local) : mémorise votre décision pour ne pas vous la redemander à chaque page. Également indispensable.
• Cookies Google Analytics (_ga, _ga_*) : mesure d'audience. Ils ne sont déposés que si vous cliquez sur « Accepter » dans le bandeau.

Aucun cookie publicitaire n'est utilisé. Refuser la mesure d'audience ne limite en rien l'utilisation du service. Vous pouvez modifier votre choix à tout moment avec le bouton « Gérer mes cookies » en bas de cette page ou le lien « Cookies » en pied de page du site.`,
  },
  {
    number: '09',
    title: 'Vos droits',
    content: `Vous disposez des droits suivants sur vos données :

• Accès : obtenir une copie de vos données.
• Rectification : corriger des données inexactes (votre nom et votre mot de passe sont aussi modifiables dans les paramètres de votre compte).
• Effacement : demander la suppression de votre compte et de vos données.
• Limitation : demander le gel temporaire d'un traitement.
• Portabilité : recevoir vos données dans un format structuré et lisible par machine.
• Opposition : vous opposer à un traitement fondé sur l'intérêt légitime.
• Retrait du consentement : à tout moment, pour la mesure d'audience ou la liste d'attente.
• Directives post-mortem : définir le sort de vos données après votre décès.

Pour exercer ces droits, écrivez à contact@cyber-guard.fr. Nous répondons dans un délai d'un mois. En cas de doute raisonnable sur votre identité, nous pouvons vous demander de la justifier.

Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL (www.cnil.fr), 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07.`,
  },
  {
    number: '10',
    title: 'Sécurité',
    content: `• Mots de passe chiffrés avec bcrypt, jamais stockés en clair.
• Communications chiffrées en HTTPS.
• Authentification par jeton et liens de réinitialisation à usage unique, valables 1 heure.
• Double authentification par application (TOTP) disponible pour tous les comptes et obligatoire pour l'accès administrateur. Le secret est chiffré en base et les codes de secours ne sont conservés que sous forme d'empreinte.
• Possibilité de fermer à distance toutes les sessions ouvertes. Les sessions sont aussi fermées automatiquement après une réinitialisation du mot de passe ou un changement de la double authentification.
• Liens de confirmation et de partage stockés uniquement sous forme d'empreinte.
• Chaque compte n'accède qu'à ses propres audits, rapports, domaines et fiches clients.
• Analyse approfondie réservée aux domaines dont la propriété a été prouvée par un enregistrement DNS, et refus des cibles pointant vers une adresse réseau interne.
• Limitation du nombre de tentatives de connexion, d'inscription et de réinitialisation.
• Accès administrateur réservé à la responsable du traitement.

Aucune mesure n'offre une sécurité absolue. En cas de violation de données présentant un risque pour vous, nous en informerons la CNIL et, si nécessaire, les personnes concernées, dans les conditions prévues par le RGPD.`,
  },
  {
    number: '11',
    title: 'Public concerné',
    content: `CyberGuard s'adresse aux professionnels (entreprises, indépendants, agences). Le service n'est pas destiné aux mineurs.`,
  },
  {
    number: '12',
    title: 'Modifications',
    content: `Cette politique peut évoluer, notamment si nous ajoutons un sous-traitant ou une fonctionnalité. La date de dernière mise à jour figure en haut de cette page. En cas de modification importante, les utilisateurs inscrits en sont informés par email.

Pour toute question : contact@cyber-guard.fr`,
  },
];

export default function PolitiqueConfidentialite() {
  useSEO('/confidentialite');
  return (
    <div className="min-h-screen bg-[#09090b] text-white">

      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-500" />
            <span className="font-mono text-lg font-black text-white uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <Link to="/" className="text-zinc-500 hover:text-white font-mono text-xs uppercase tracking-widest transition-colors">
            ← Retour
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16 space-y-12">

        {/* Titre */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20">
            <Lock className="w-3 h-3 text-blue-400" />
            <span className="text-blue-400 text-[10px] font-mono uppercase tracking-widest">RGPD — Protection des données</span>
          </div>
          <h1 className="text-4xl font-black font-mono uppercase tracking-tighter text-white">
            Politique de Confidentialité
          </h1>
          <p className="text-zinc-400 font-mono text-xs">Dernière mise à jour : 15 octobre 2026</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.number} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center rounded-sm shrink-0">
                  <span className="text-blue-400 font-mono text-[10px] font-black">{section.number}</span>
                </div>
                <h2 className="text-white font-mono font-bold text-sm uppercase tracking-widest">
                  {section.title}
                </h2>
              </div>
              <div className="pl-11">
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Gestion des cookies */}
        <div className="text-center">
          <button
            onClick={openCookieConsent}
            className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 text-white hover:bg-zinc-800 font-mono text-xs uppercase font-bold transition-colors"
          >
            <Cookie className="w-4 h-4" /> Gérer mes cookies
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 pt-8 flex items-center justify-between">
          <span className="text-zinc-400 font-mono text-xs uppercase">CyberGuard — Politique de Confidentialité — 2026</span>
          <Link to="/" className="text-blue-500 hover:text-blue-400 font-mono text-xs uppercase transition-colors">
            Retour à l'accueil →
          </Link>
        </div>
      </main>
    </div>
  );
}
